"use client";
import {
  Cog,
  GraduationCap,
  Layout,
  Lightbulb,
  Phone,
  TrendingUp,
  Wrench,
} from "lucide-react";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import ProductPage from "@/components/ProductPage";
import ImageSupport from "../../../public/support1.jpg";


const services = [
  {
    icon: <Phone className="h-6 w-6" />,
    title: "Erreichbarkeit",
    text: (
      <>
        Unser Support-Team ist werktags für Sie erreichbar – die jeweiligen
        Servicezeiten entnehmen Sie bitte{" "}
        <a href="/support" className="text-brand-primary font-bold underline">
          hier
        </a>
        . Über unsere sichere Fernwartungslösung können wir schnell und direkt
        eingreifen, um eventuelle Probleme gemeinsam mit Ihnen zu lösen.
      </>
    ),
  },
  {
    icon: <Cog className="h-6 w-6" />,
    title: "Projektierung",
    text: "Für einen erfolgreichen Projektverlauf ist eine präzise und strukturierte Planung unerlässlich. Unsere zielgerichtete Projektierung gewährleistet eine nachhaltige und effiziente Implementierung Ihrer neuen Unternehmenssoftware.",
  },
  {
    icon: <Lightbulb className="h-6 w-6" />,
    title: "Consulting",
    text: "Unsere erfahrenen Consultants begleiten Sie engagiert bei der Umsetzung anspruchsvoller und individueller Projekte – ob bei der Implementierung, während der Projektphase oder im anschließenden Support.",
  },
  {
    icon: <Wrench className="h-6 w-6" />,
    title: "Customizing",
    text: "Wir entwickeln auf Wunsch individuelle Erweiterungen, übernehmen Reportanpassungen und integrieren zusätzliche Schnittstellen, damit Ihre Lösung exakt Ihren Anforderungen entspricht.",
  },
  {
    icon: <Phone className="h-6 w-6" />,
    title: "Support",
    text: "Unser geschultes Support-Team ist über unsere Hotline für Sie erreichbar. Und auch außerhalb unserer Arbeitszeiten stehen Ihnen verschiedene Möglichkeiten zur Verfügung, um Unterstützung zu erhalten.",
  },
  {
    icon: <GraduationCap className="h-6 w-6" />,
    title: "Schulungen",
    text: "Unsere Experten vermitteln Ihnen in praxisorientierten Kleingruppen den sicheren Umgang mit unserer Software – für effizienten Einsatz und maximalen Nutzen.",
  },
  {
    icon: <Layout className="h-6 w-6" />,
    title: "Agentur",
    text: "Unser Team aus kreativen Designern und erfahrenen Webentwicklern unterstützt Sie bei der Umsetzung Ihres Webshops – vom Konzept über das Design bis hin zum perfekt funktionierenden Onlineauftritt.",
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: "SEO & SEA",
    text: "Um kontinuierlich den aktuellen Google-Standards zu entsprechen, unterstützen wir Sie mit einer fundierten, projektbegleitenden SEO- und SEA-Beratung – für eine langfristig erfolgreiche Sichtbarkeit Ihrer Website.",
  },
];

export default function Support() {
  return (
    <>
      <ProductPage
        productName="Support & Service – SDI Group"
        hero={{
          title: "Support & Service",
          subtitle:
            "Wir begleiten Sie von der ersten Beratung bis zum laufenden Betrieb – mit zuverlässigem Support, fundierter Beratung und individuellen Lösungen.",
          buttonLeft: {
            title: "Kontakt aufnehmen",
            href: "/kontakt",
          },
          buttonRight: {
            title: "Support-Zeiten",
            href: "/support",
          },
          image: ImageSupport,
          imageAlt: "Support & Service",
        }}
        highlight={{
          image: ImageSupport,
          bullets: [
            <>
              <strong>Persönlicher Support</strong> – werktags erreichbar per
              Telefon und Fernwartung.
            </>,
            <>
              <strong>Schulungen & Consulting</strong> – praxisnah und
              individuell auf Ihr Team zugeschnitten.
            </>,
            <>
              <strong>Agentur & SEO</strong> – von der Webshop-Umsetzung bis zur
              langfristigen Sichtbarkeit.
            </>,
          ],
        }}
        beforeVideo={
          <Section className="bg-white py-4 md:py-6 mt-12">
            <ImageText
              imagePosition="right"
              imageSlot={
                <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                  <video className="h-full w-full object-cover" controls>
                    <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                  </video>
                </div>
              }
              title="Gemeinsam zu stabilen IT-Lösungen"
            >
              <p className="text-slate-700 font-bold text-lg leading-relaxed">
                Wir unterstützen Sie ganzheitlich: Wir planen gemeinsam mit Ihnen und kümmern uns im laufenden Betrieb um alle Details. Mit persönlicher Beratung und schnellen Reaktionszeiten sorgen wir dafür, dass Ihre Technik im Hintergrund perfekt funktioniert, damit Sie sich voll und ganz auf Ihr Kerngeschäft konzentrieren können.
              </p>

              <p className="mt-4 text-slate-700 leading-relaxed">
                Unser Ziel: reibungslose Prozesse, schnelle Hilfe und
                langfristiger Erfolg Ihrer Systeme.
              </p>
            </ImageText>

            {/* DETAILTEXTE */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {services.map((service, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-brand-primary text-white">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-slate-700 text-sm leading-relaxed mt-1">
                      {service.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        }
        faqs={[
  {
    q: "Wie erreiche ich den Support?",
    a: "Unser Support-Team ist Montag bis Freitag von 09:30–13:00 Uhr und 14:00–18:00 Uhr telefonisch und per E-Mail unter info@sdigroup.de erreichbar.",
  },
  {
    q: "Bieten Sie auch Fernwartung an?",
    a: "Ja, über unsere sichere Fernwartungslösung mit TeamViewer, AnyDesk oder RustDesk können wir direkt und schnell eingreifen – selbstverständlich nur mit Ihrer ausdrücklichen Erlaubnis.",
  },
  {
    q: "Sind Schulungen auch für Einsteiger geeignet?",
    a: "Ja, unsere Schulungen finden in praxisorientierten Kleingruppen statt und richten sich an alle Erfahrungsstufen – vom kompletten Einsteiger bis zum erfahrenen Anwender.",
  },
  {
    q: "Können Sie auch unseren Webshop umsetzen?",
    a: "Ja, unser Agentur-Team begleitet Sie vom Konzept über das Design bis zum fertigen Onlineauftritt – inklusive Anbindung an bestehende Systeme wie Warenwirtschaft oder Buchhaltung.",
  },
  {
    q: "Wie schnell wird mein Anliegen im Support bearbeitet?",
    a: "Wir bemühen uns, alle Anfragen so schnell wie möglich zu bearbeiten – telefonische Anfragen werden direkt beantwortet, E-Mails in der Regel noch am selben Werktag.",
  },
  {
    q: "Bieten Sie auch individuelle Schulungen für unser Team an?",
    a: "Ja, auf Wunsch bieten wir individuelle Schulungen direkt bei Ihnen per Fernwartung an – abgestimmt auf Ihre Software und Ihre spezifischen Abläufe.",
  },
  {
    q: "Was passiert, wenn ich nach der Schulung noch Fragen habe?",
    a: "Unser Support-Team steht Ihnen auch nach der Schulung jederzeit zur Verfügung – per Telefon oder E-Mail, damit Sie im Alltag sicher mit unserer Software arbeiten können.",
  },
]}
        features={[]}
      />
    </>
  );
}
